import {
	Alert,
	Bullseye,
	Button,
	Card,
	CardBody,
	Content,
	ContentVariants,
	EmptyState,
	EmptyStateVariant,
	Grid,
	Split,
	SplitItem,
	Stack,
	StackItem,
} from "@patternfly/react-core";
import { CheckCircleIcon, ErrorCircleOIcon, ExclamationTriangleIcon } from "@patternfly/react-icons";
import { map } from "lodash";
import { type FC, Fragment } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import type { ICheckListActionItems } from "../../constants/pfsContants";
import type { ProductRouteParams } from "../../constants/routerconstants";
import "./CheckList.scss";
import Uri from "jsuri";
import { useChecklistStatus } from "../../queries/productDetails";
import Env from "../../utils/env";
import { SkeletonContainer } from "./Skeleton";

export const CheckList: FC = () => {
	const match = useRouteMatch<ProductRouteParams>();
	const history = useHistory();
	const { data: checklistStatus, isFetching: isDataFetching, error } = useChecklistStatus(match.params.productId);
	if (error) {
		return (
			<Bullseye>
				<EmptyState
					headingLevel="h5"
					icon={ErrorCircleOIcon}
					titleText={error?.message}
					variant={EmptyStateVariant.full}
				></EmptyState>
			</Bullseye>
		);
	}
	const getCheckListStatus = (status: string) => {
		switch (status) {
			case "NOT_COMPLETED":
				return <div className="rhc-overview-checklist-wrapper__circle-icon" />;
			case "COMPLETED":
				return (
					<div className="rhc-overview-checklist-wrapper__circle-icon">
						<CheckCircleIcon className="rhc-overview-checklist-wrapper__completed-icon" />
					</div>
				);
			case "ERROR":
				return (
					<div className="rhc-overview-checklist-wrapper__circle-icon">
						<ExclamationTriangleIcon className="rhc-overview-checklist-wrapper__error-icon" />
					</div>
				);
			default:
				return null;
		}
	};
	const showError = checklistStatus?.items?.some((item) =>
		item?.checklistItems?.some((checklistItem) => checklistItem?.status === "ERROR"),
	);
	return (
		<Card className="rhc-overview-checklist-wrapper" isFullHeight>
			<CardBody>
				{isDataFetching ? (
					<SkeletonContainer />
				) : (
					<Grid hasGutter span={12}>
						<Stack hasGutter>
							<StackItem>
								<Content>
									<Content component={ContentVariants.h2}> Product is unpublished</Content>
									<Content component="p">{checklistStatus?.items[0]?.description}</Content>
								</Content>
							</StackItem>
							{showError && (
								<StackItem>
									<Alert variant="warning" isInline title="Problem retrieving checklist">
										<p>
											We were unable to load some checklist items. Refresh the page to try again. If the issue persists,
											create a{" "}
											<Button
												isInline
												variant={"link"}
												onClick={() => {
													window.open(`${Env.padCaseListUrl}`);
												}}
											>
												support ticket
											</Button>
											.
										</p>
									</Alert>
								</StackItem>
							)}
							{checklistStatus?.items?.map((mainChecklist) => (
								<Fragment key={mainChecklist.id}>
									{mainChecklist?.checklistItems
										?.sort((a, b) => Number(a?.displayOrder || 0) - Number(b.displayOrder || 0))
										?.map((elm) => (
											<StackItem key={elm?.id}>
												<Split hasGutter>
													<SplitItem>{getCheckListStatus(elm.status)}</SplitItem>
													<SplitItem isFilled style={{ padding: "4px" }}>
														{elm.title}
													</SplitItem>
													<SplitItem>
														{elm.status === "ERROR" ? (
															<Content>
																<Content component="p">Unable to load item</Content>
															</Content>
														) : (
															map(elm.checklistItemActions, (element: ICheckListActionItems, index) => (
																<Button
																	key={index}
																	onClick={() => {
																		const productDetailsPrefix = new Uri(Env.ConnectPortalUrls[Env.getEnvName()])
																			.setPath(`${Env.routePrefix}`)
																			.toString();
																		if (element?.link?.startsWith(productDetailsPrefix)) {
																			const checkListLink = new Uri(element?.link);
																			history.push(checkListLink.path());
																		} else {
																			window.location.href = element?.link;
																		}
																	}}
																	className="rhc-overview-checklist-wrapper__action-btn"
																	variant={element?.label !== "Review" ? "secondary" : "link"}
																>
																	{element?.label}
																</Button>
															))
														)}
													</SplitItem>
												</Split>
											</StackItem>
										))}
								</Fragment>
							))}
						</Stack>
					</Grid>
				)}
			</CardBody>
		</Card>
	);
};
